// src/api/customer.ts
// API functions for customer authentication and account management.

import { ApiResult } from "../types/ApiResult";

// ---------------------------------------------------------------------------
// Auth helpers
// ---------------------------------------------------------------------------

function authHeaders(authToken: string): HeadersInit {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${authToken}`,
  };
}

// ---------------------------------------------------------------------------
// Auth — passwordless OTP flow
// ---------------------------------------------------------------------------

/** Send a 6-digit OTP to the customer's email. Returns an otpToken to use at verify time.
 *  On rate-limit (429) the error response also includes `retryAfter` (seconds). */
export const sendCustomerOtp = async (
  email: string,
): Promise<ApiResult<{ otpToken: string }> & { retryAfter?: number }> => {
  try {
    const res = await fetch("/.netlify/functions/customer-send-otp", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    const body = await res.json();
    return body;
  } catch (err) {
    return { error: `Failed to send code: ${err}` };
  }
};

/** Verify the OTP. Returns an auth token + basic customer data on success. */
export const verifyCustomerOtp = async (
  email: string,
  otp: string,
  otpToken: string,
): Promise<
  ApiResult<{
    authToken: string;
    customer: {
      customerId: string | null;
      email: string;
      firstName: string;
      lastName: string;
      phone: string;
      /** Server-decided admin flag; drives the UX-only /admin gate. */
      isAdmin?: boolean;
    };
  }>
> => {
  try {
    const res = await fetch("/.netlify/functions/customer-verify-otp", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, otp, otpToken }),
    });
    // `await` here, not a bare `return res.json()`: returning the promise hands
    // the rejection to the CALLER, past this catch. A 502 or any non-JSON error
    // page then rejects inside whichever handler awaited us, and both callers
    // (AccountLoginPage, CheckoutAccountPanel) render errors from an `{ error }`
    // result — so the buyer would get a dead button and no message at all.
    return await res.json();
  } catch (err) {
    return { error: `Failed to verify code: ${err}` };
  }
};

// ---------------------------------------------------------------------------
// Profile
// ---------------------------------------------------------------------------

export interface SavedAddress {
  addressLine1?: string;
  addressLine2?: string;
  locality?: string;
  administrativeDistrictLevel1?: string;
  postalCode?: string;
  country?: string;
}

export interface CustomerProfile {
  id: string | null;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  note?: string;
  createdAt?: string;
  marketingAllowed?: boolean;
  address?: SavedAddress;
}

/** Fetch the authenticated customer's Square profile. */
export const getCustomerProfile = async (
  authToken: string,
): Promise<ApiResult<CustomerProfile>> => {
  try {
    const res = await fetch("/.netlify/functions/get-customer-profile", {
      method: "GET",
      headers: authHeaders(authToken),
    });
    return res.json();
  } catch (err) {
    return { error: `Failed to fetch profile: ${err}` };
  }
};

/** Update first name, last name, and phone. */
export const updateCustomerProfile = async (
  authToken: string,
  data: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    marketingAllowed?: boolean;
    address?: SavedAddress;
  },
): Promise<ApiResult<CustomerProfile>> => {
  try {
    const res = await fetch("/.netlify/functions/update-customer-profile", {
      method: "POST",
      headers: authHeaders(authToken),
      body: JSON.stringify(data),
    });
    return res.json();
  } catch (err) {
    return { error: `Failed to update profile: ${err}` };
  }
};

// ---------------------------------------------------------------------------
// Saved cards ("Save my information for a faster checkout")
// ---------------------------------------------------------------------------

/** A card on file in Square. Brand/last4/expiry is everything Square ever
 *  returns — no PAN exists on our side of the boundary. */
export interface SavedCard {
  id: string;
  cardBrand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
  cardholderName: string | null;
  enabled: boolean;
}

/** Fetch the authenticated customer's saved cards (empty list if none). */
export const getCustomerCards = async (
  authToken: string,
): Promise<ApiResult<SavedCard[]>> => {
  try {
    const res = await fetch("/.netlify/functions/get-customer-cards", {
      method: "GET",
      headers: authHeaders(authToken),
    });
    return res.json();
  } catch (err) {
    return { error: `Failed to fetch saved cards: ${err}` };
  }
};

/**
 * Store the card used for `paymentId` against the customer's Square profile.
 *
 * Called after the payment is AUTHORIZED (the payment id is the CreateCard
 * source — see netlify/functions/save-customer-card.js). Best-effort by
 * definition: the sale has already happened, so a failure here must never
 * surface as a checkout error.
 */
export const saveCustomerCard = async (
  authToken: string,
  paymentId: string,
): Promise<ApiResult<{ card?: SavedCard; alreadySaved?: boolean }>> => {
  try {
    const res = await fetch("/.netlify/functions/save-customer-card", {
      method: "POST",
      headers: authHeaders(authToken),
      body: JSON.stringify({ paymentId }),
    });
    return res.json();
  } catch (err) {
    return { error: `Failed to save card: ${err}` };
  }
};

/** Remove (disable) a saved card. Irreversible in Square — see the function. */
export const deleteCustomerCard = async (
  authToken: string,
  cardId: string,
): Promise<ApiResult<{ id: string; removed: boolean }>> => {
  try {
    const res = await fetch("/.netlify/functions/delete-customer-card", {
      method: "POST",
      headers: authHeaders(authToken),
      body: JSON.stringify({ cardId }),
    });
    return res.json();
  } catch (err) {
    return { error: `Failed to remove card: ${err}` };
  }
};

// ---------------------------------------------------------------------------
// Orders
// ---------------------------------------------------------------------------

/** Fetch all orders for the authenticated customer. */
export const getCustomerOrders = async (
  authToken: string,
): Promise<ApiResult<any[]>> => {
  try {
    const res = await fetch("/.netlify/functions/get-customer-orders", {
      method: "GET",
      headers: authHeaders(authToken),
    });
    return res.json();
  } catch (err) {
    return { error: `Failed to fetch orders: ${err}` };
  }
};

// ---------------------------------------------------------------------------
// Reviews
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Return Requests
// ---------------------------------------------------------------------------

export type ReturnStatus =
  | "requested"
  | "approved"
  | "received"
  | "denied"
  | "refunded"
  | "canceled";

export interface ReturnRequestItem {
  name: string;
  quantity: number;
  /** Per-line value in integer cents (used admin-side for refund suggestions). */
  cents?: number;
}

export interface ReturnRequest {
  id: string;
  originalOrderId: string;
  reason: string;
  notes: string;
  items: ReturnRequestItem[];
  status: ReturnStatus;
  adminNote: string;
  createdAt: string;
  updatedAt: string;
}

/** Fetch all return/refund requests for the authenticated customer. */
export const getCustomerReturns = async (
  authToken: string,
): Promise<ApiResult<ReturnRequest[]>> => {
  try {
    const res = await fetch("/.netlify/functions/get-customer-returns", {
      method: "GET",
      headers: authHeaders(authToken),
    });
    return res.json();
  } catch (err) {
    return { error: `Failed to fetch returns: ${err}` };
  }
};

/**
 * Cancel one of the authenticated customer's own return requests (only allowed
 * while it's still `requested` or `approved`). Sets the record to `canceled`
 * and emails a confirmation. Returns the updated record.
 */
export const cancelCustomerReturn = async (
  authToken: string,
  id: string,
): Promise<ApiResult<ReturnRequest>> => {
  try {
    const res = await fetch("/.netlify/functions/cancel-return-request", {
      method: "POST",
      headers: authHeaders(authToken),
      body: JSON.stringify({ id }),
    });
    return res.json();
  } catch (err) {
    return { error: `Failed to cancel return: ${err}` };
  }
};

/** Submit a review for an order (1-5 stars + optional text). */
export const submitOrderReview = async (
  authToken: string,
  orderId: string,
  rating: number,
  reviewText: string,
): Promise<ApiResult<{ success: boolean }>> => {
  try {
    const res = await fetch("/.netlify/functions/submit-order-review", {
      method: "POST",
      headers: authHeaders(authToken),
      body: JSON.stringify({ orderId, rating, reviewText }),
    });
    return res.json();
  } catch (err) {
    return { error: `Failed to submit review: ${err}` };
  }
};
